import { getHarmonyBaseId } from '@/composables/saju-insight/useAnalysisSajuContext';
import { getAnalysisLogAPI, getAnalysisAccessAPI } from '@/api/AnalysisApi';
import { defineStore } from 'pinia';

export const useAnalysisStore = defineStore('analysis', () => {
  /** 선택된 메뉴 */
  const selectedMenu = ref(localStorage.getItem('analysisSelectedMenu'));

  watch(selectedMenu, (newValue) => {
    if (newValue) {
      localStorage.setItem('analysisSelectedMenu', newValue);
    } else {
      localStorage.removeItem('analysisSelectedMenu');
    }
  });

  const getSelectedMenu = computed(() => {
    return selectedMenu.value || '/summary';
  });

  /** 하위메뉴 열림 상태 */
  const isMenuOpen = ref({});

  /** 선택된 메뉴 초기화 (메인에서 진입 시 사용) */
  const resetSelectedMenu = () => {
    selectedMenu.value = '/overviewSajuSummary';
  };

  /** 선택 메뉴 화면 열기 */
  const clickMainMenu = (view) => {
    selectedMenu.value = view;
  };

  /** 하위 메뉴 열림버튼 클릭 */
  const toggleSubmenu = (id) => {
    isMenuOpen.value[id] = !isMenuOpen.value[id];
  };

  /** 우측 탭과 데이터 공유 */
  // 내사주분석
  const analysisResult = ref(null);
  const setAnalysisResult = (data) => {
    analysisResult.value = data;
  };
  const resetAnalysisResult = () => {
    analysisResult.value = null;
  };
  // 내사주 분석 통계 (추후 하나로 합쳐짐)
  const analStastResult = ref(null);
  const setAnalStastResult = (data) => {
    analStastResult.value = data;
  };
  const resetAnalstastResult = () => {
    analStastResult.value = null;
  };

  /** 사주이름 */
  const userName = ref('-');
  const setUserName = (name) => {
    userName.value = name;
  };

  /** 목차 선택시 해당 위치로 이동 */
  const selectedContentIndex = ref(null);
  const setSelectedContentIndex = (newIndex) => (selectedContentIndex.value = newIndex);

  const navigateToContent = (menuPath, contentIdx = null) => {
    selectedMenu.value = menuPath;
    nextTick(() => {
      selectedContentIndex.value = contentIdx;
      if (contentIdx !== null) {
        localStorage.setItem('analysisContentIdx', contentIdx);
      } else {
        localStorage.removeItem('analysisContentIdx');
      }
    });
  };

  // children 활용 ver
  const selectChildMenu = (view, contentIdx, child) => {
    if (!child) {
      selectedMenu.value = view;
      localStorage.setItem('analysisSelectedMenu', view);
    } else {
      selectedMenu.value = child;
      localStorage.setItem('analysisSelectedMenu', child);
    }

    if (contentIdx !== null) {
      selectedContentIndex.value = contentIdx;
      localStorage.setItem('analysisContentIdx', contentIdx);
    } else {
      selectedContentIndex.value = null;
      localStorage.removeItem('analysisContentIdx');
    }
  };

  /** 성향분석 index */
  const personalityIndex = ref(1);
  const setPersonalityIndex = (newIndex) => (personalityIndex.value = newIndex);

  /** 용신분석 index */
  const yongsinIndex = ref(1);
  const setYongsinIndex = (newIndex) => (yongsinIndex.value = newIndex);

  /** 낭만월운 index */
  const monthlyFortuneIndex = ref(1);
  const setMonthlyFortuneIndex = (newIndex) => (monthlyFortuneIndex.value = newIndex);

  /** 낭만월운 선택 월 (CoverPage / CoverPanel 공유) */
  const _mfNow = new Date();
  const monthlyFortuneSelectedYear = ref(_mfNow.getFullYear());
  const monthlyFortuneSelectedMonth = ref(_mfNow.getMonth() + 1);
  const setMonthlyFortuneSelectedMonth = (year, month) => {
    monthlyFortuneSelectedYear.value = year;
    monthlyFortuneSelectedMonth.value = month;
  };

  /**
   * 결제 완료 후 자동으로 열어야 할 낭만월운 카드 정보.
   * 결제 직후 parent(analysis/index.vue)가 componentKey 를 증가시켜 cover 를 remount 하므로,
   * 결제 시점에 열어둔 drawer 상태가 사라진다. 결제한 카드 정보를 여기에 저장해두면
   * remount 직후 CoverPage 의 onMounted 에서 다시 drawer 를 열 수 있다.
   */
  const monthlyFortunePendingDrawerCard = ref(null);
  const setMonthlyFortunePendingDrawerCard = (card) => {
    monthlyFortunePendingDrawerCard.value = card;
  };
  const consumeMonthlyFortunePendingDrawerCard = () => {
    const c = monthlyFortunePendingDrawerCard.value;
    monthlyFortunePendingDrawerCard.value = null;
    return c;
  };

  /**
   * 결제 직후 remount 시 선택월을 유지해야 함을 나타내는 플래그.
   * 전체 패키지 구매처럼 drawer 를 자동 오픈할 필요는 없지만 선택월은 보존해야 하는 케이스에 사용.
   */
  const monthlyFortunePreserveSelectedMonth = ref(false);
  const setMonthlyFortunePreserveSelectedMonth = () => {
    monthlyFortunePreserveSelectedMonth.value = true;
  };
  const consumeMonthlyFortunePreserveSelectedMonth = () => {
    const v = monthlyFortunePreserveSelectedMonth.value;
    monthlyFortunePreserveSelectedMonth.value = false;
    return v;
  };

  /** Panel/구매내역에서 카드 클릭 시 CoverPage의 drawer를 여는 요청 트리거 */
  const monthlyFortuneOpenDrawerRequest = ref(0);
  const monthlyFortuneRequestedDrawerCardKey = ref(null);
  const requestMonthlyFortuneOpenDrawer = (cardKey) => {
    monthlyFortuneRequestedDrawerCardKey.value = cardKey;
    monthlyFortuneOpenDrawerRequest.value += 1;
  };

  /** 신년운세 index */
  const yearlyFortuneIndex = ref(1);
  const setYearlyFortuneIndex = (newIndex) => (yearlyFortuneIndex.value = newIndex);

  /** 낭만궁합 index */
  const nangmanHarmonyIndex = ref(1);
  const setNangmanHarmonyIndex = (newIndex) => (nangmanHarmonyIndex.value = newIndex);

  /** 용신라이트 index */
  const yongsinLightIndex = ref(1);
  const setYongsinLightIndex = (newIndex) => (yongsinLightIndex.value = newIndex);

  /** 용신라이트 API 데이터 (DetailPage ↔ DetailPanel 공유) */
  const yongsinLightApiData = ref(null);
  const setYongsinLightApiData = (data) => (yongsinLightApiData.value = data);

  /** 낭만궁합 결제 데이터 (cover → detailPanel 전달용) */
  const harmonyPaymentData = ref(null);
  const setHarmonyPaymentData = (data) => (harmonyPaymentData.value = data);
  const resetHarmonyPaymentData = () => (harmonyPaymentData.value = null);

  /** 낭만궁합 선택 초기화 플래그 (detail → overview 복귀 시 자동채움 방지) */
  const harmonyResetSelection = ref(false);
  const requestHarmonyResetSelection = () => (harmonyResetSelection.value = true);
  const consumeHarmonyResetSelection = () => {
    const val = harmonyResetSelection.value;
    harmonyResetSelection.value = false;
    return val;
  };

  /** 낭만궁합 선택 상태 (HarmonyServiceSelect에서 설정, 검증용) */
  const harmonySelectedCategory = ref(null);
  const harmonyPersonCount = ref(0);
  const setHarmonySelectionState = (category, personCount) => {
    harmonySelectedCategory.value = category;
    harmonyPersonCount.value = personCount;
  };

  /** 낭만궁합 현재 선택 조합의 결제 여부 + 매칭된 로그 (CoverPanelHarmony에서 설정) */
  const harmonyIsPurchased = ref(false);
  const harmonyMatchedLog = ref(null);
  const setHarmonyPurchaseState = (isPurchased, matchedLog = null) => {
    harmonyIsPurchased.value = isPurchased;
    harmonyMatchedLog.value = matchedLog;
  };

  /** 낭만궁합 추가해석 구매 완료 목록 + 콘텐츠 (DetailPanel ↔ ContentsHarmony03 공유) */
  const harmonyPurchasedExtraIds = ref(new Set());
  const harmonyExtraContents = ref({});
  const setHarmonyPurchasedExtraIds = (ids) => {
    harmonyPurchasedExtraIds.value = ids instanceof Set ? ids : new Set(ids);
  };
  const setHarmonyExtraContent = (id, content) => {
    harmonyExtraContents.value = { ...harmonyExtraContents.value, [id]: content };
  };
  const resetHarmonyExtras = () => {
    harmonyPurchasedExtraIds.value = new Set();
    harmonyExtraContents.value = {};
  };
  /** 구독자 추가해석 자동 조회 진행 중 여부 (로딩 표시용) */
  const harmonyExtrasAutoLoading = ref(false);
  const setHarmonyExtrasAutoLoading = (v) => (harmonyExtrasAutoLoading.value = v);

  /** 낭만궁합 결제 팝업 트리거 (ContentsHarmony → DetailPanelHarmony 간 통신) */
  const harmonyPaymentPopupTrigger = ref(0);
  const triggerHarmonyPaymentPopup = () => {
    harmonyPaymentPopupTrigger.value++;
  };

  /** 낭만궁합 카테고리 결제 트리거 (DetailPageHarmony → DetailPanelHarmony 간 통신) */
  const harmonyCategoryPaymentTarget = ref(null);
  const triggerHarmonyCategoryPayment = (cat) => {
    harmonyCategoryPaymentTarget.value = cat;
  };
  const clearHarmonyCategoryPaymentTarget = () => {
    harmonyCategoryPaymentTarget.value = null;
  };

  /** 낭만궁합 추가해석 결제 팝업 트리거 (ContentsHarmony03 mood card → DetailPanelHarmony) */
  const harmonyExtraPaymentTarget = ref(null);
  const triggerHarmonyExtraPayment = (targetId) => {
    harmonyExtraPaymentTarget.value = targetId;
  };
  const clearHarmonyExtraPaymentTarget = () => {
    harmonyExtraPaymentTarget.value = null;
  };

  /** 낭만궁합 추가해석 가격 (DetailPanelHarmony → ContentsHarmony03 공유) */
  const harmonyExtraYearlyPrice = ref(null);
  const harmonyExtraMonthlyPrice = ref(null);
  const setHarmonyExtraPrices = (yearly, monthly) => {
    harmonyExtraYearlyPrice.value = yearly;
    harmonyExtraMonthlyPrice.value = monthly;
  };

  /** 상세분석 이용내역 목록 (하위 호환용) */
  const analysisLogData = ref(null);
  /** 상세분석 이용내역 목록 (typeId별 분리 저장) */
  const analysisLogByType = ref({});
  /** 분석권 불러오기 상태값 */
  const loadingTicketInfo = ref(true);

  /**
   * 낭만월운 월별 로그 캐시
   * 구조: { '<guestUserId>-YYYY-M': { [typeId]: [...logs] } }
   *   - typeId 10(부모/전체구매) + 자식 typeId(42~46) 로그가 같이 들어옴
   *   - 월 단위 권한이고 사용자별로 분리해야 하므로 (guestUserId, year, month) 키 사용
   */
  const monthlyFortuneLogsByMonth = ref({});

  /**
   * 낭만월운 월별 접근 자격 캐시 (/analysis/access 기반, 권한 판단의 1순위)
   * 구조: { '<guestUserId>-YYYY-M': { [typeId]: accessItem } }
   *   - canAccess 에 분석권 + 구독 + 부모→자식 접근이 모두 반영됨
   *   - 월별로 canAccess 가 달라 월 키로 분리 (analysisAccessByType 평면 맵과 별개)
   */
  const monthlyFortuneAccessByMonth = ref({});

  /** 캐시 키 빌더 */
  const buildMonthlyFortuneCacheKey = (guestUserId, year, month) =>
    `${guestUserId}-${year}-${month}`;

  /**
   * 낭만월운 월별 권한 로드.
   * 권한 판단은 /analysis/access(canAccess)로 일원화하므로 per-month /analysis/log 호출은 제거하고
   * access 만 조회한다. (구매내역/구매한 달 목록은 fetchMonthlyFortunePurchaseHistory(log)에서 별도 로드)
   * 함수명은 호출부 호환을 위해 유지.
   */
  const fetchMonthlyFortuneLogs = async (guestUserId, year, month, opts = {}) => {
    await fetchMonthlyFortuneAccess(guestUserId, year, month, opts);
  };

  /**
   * 낭만월운 월별 접근 자격 조회 (/analysis/access, includeChildTypes + year/month)
   * 부모(10) + 자식(42~46) 의 canAccess 를 월 키로 캐시. 권한 판단의 1순위 소스.
   * 이미 캐시된 월이면 재조회하지 않음.
   */
  const fetchMonthlyFortuneAccess = async (guestUserId, year, month, { force = false } = {}) => {
    if (!guestUserId || !year || !month) return;
    const cacheKey = buildMonthlyFortuneCacheKey(guestUserId, year, month);
    // force=true 면 캐시 무시 재조회 (구매 직후 해당 월 권한 갱신용)
    if (!force && monthlyFortuneAccessByMonth.value[cacheKey]) return;
    try {
      const response = await getAnalysisAccessAPI({
        guestUserId,
        analysisTypeIds: [10],
        includeChildTypes: true,
        year,
        month,
      });
      const byType = {};
      (response?.items || []).forEach((item) => {
        byType[item.analysisTypeId] = item;
      });
      monthlyFortuneAccessByMonth.value = {
        ...monthlyFortuneAccessByMonth.value,
        [cacheKey]: byType,
      };
    } catch (e) {
      // 실패 시 캐시 미설정 → 권한 판단은 logData 폴백으로 동작
    }
  };

  /**
   * 낭만월운 특정 (guestUserId, year, month, typeId) 권한 보유 여부
   * 1순위: access 의 canAccess (분석권 + 구독 + 부모→자식 접근 통합, 백엔드 계산)
   * 폴백: access 미로드/실패 시 기존 로그 판단(부모 10 또는 해당 자식 로그)
   */
  const hasMonthlyFortuneAccess = (guestUserId, year, month, typeId) => {
    if (!guestUserId) return false;
    const cacheKey = buildMonthlyFortuneCacheKey(guestUserId, year, month);

    // 1순위: access canAccess (부모 구매 → 자식 접근까지 백엔드가 반영)
    const accessByType = monthlyFortuneAccessByMonth.value[cacheKey];
    if (accessByType && typeId && accessByType[typeId]) {
      return accessByType[typeId].canAccess === true;
    }

    // 폴백: 기존 로그 판단
    const monthLogs = monthlyFortuneLogsByMonth.value[cacheKey];
    if (!monthLogs) return false;
    if (monthLogs[10]?.length > 0) return true;
    if (typeId && monthLogs[typeId]?.length > 0) return true;
    return false;
  };

  /**
   * 특정 (guestUserId, year, month) 의 권한(access) 캐시가 있는지
   * (fetchMonthlyFortuneLogs 가 access 를 채우므로 재조회 가드를 access 캐시 기준으로 본다)
   */
  const hasMonthlyFortuneLogsCache = (guestUserId, year, month) => {
    if (!guestUserId) return false;
    return !!monthlyFortuneAccessByMonth.value[
      buildMonthlyFortuneCacheKey(guestUserId, year, month)
    ];
  };

  /** 낭만월운 자식 카드 typeId 목록 (42~46) */
  const MONTHLY_FORTUNE_CHILD_TYPE_IDS = [42, 43, 44, 45, 46];

  /**
   * 낭만월운 구매이력 전체 캐시 (sessionUser → guestUser 단위)
   * 구조: { [guestUserId]: [logs] }
   * 부모(typeId 10) + 자식(42~46) 로그가 모두 들어옴.
   */
  const monthlyFortunePurchaseHistory = ref({});

  /**
   * 낭만월운 구매이력 전체 조회
   * - typeId 10 + 자식(42~46) 의 유효 로그를 기간 제한 없이 받아옴
   * - 결과를 monthlyFortunePurchaseHistory 와 monthlyFortuneLogsByMonth(월별 캐시)에 함께 반영
   */
  const fetchMonthlyFortunePurchaseHistory = async (guestUserId) => {
    if (!guestUserId) return;
    try {
      const params = {
        guestUserId,
        analysisTypeId: 10,
        includeChildTypes: true,
        isValid: true,
        page: 1,
        size: -1,
      };
      const response = await getAnalysisLogAPI(params);
      const content = response?.content || [];
      monthlyFortunePurchaseHistory.value = {
        ...monthlyFortunePurchaseHistory.value,
        [guestUserId]: content,
      };
      // 월별 캐시에도 채워두면 3개월 외 달도 권한 판정 가능
      const merged = { ...monthlyFortuneLogsByMonth.value };
      const byMonth = {};
      content.forEach((log) => {
        if (!log.year || !log.month) return;
        const k = buildMonthlyFortuneCacheKey(guestUserId, log.year, log.month);
        if (!byMonth[k]) byMonth[k] = {};
        if (!byMonth[k][log.analysisTypeId]) byMonth[k][log.analysisTypeId] = [];
        byMonth[k][log.analysisTypeId].push(log);
      });
      Object.entries(byMonth).forEach(([k, v]) => {
        merged[k] = v;
      });
      monthlyFortuneLogsByMonth.value = merged;
    } catch (e) {
      monthlyFortunePurchaseHistory.value = {
        ...monthlyFortunePurchaseHistory.value,
        [guestUserId]: [],
      };
    }
  };

  /**
   * 해당 (guestUserId, year, month) 가 "전체 접근 가능"(전체구매·구독 포함) 한지
   * 1순위: access — 부모(10) canAccess(전체구매/구독) 또는 자식(42~46) 전부 canAccess
   * 폴백: access 미로드/실패 시 기존 로그 판단
   * (구매 팝업에서 이미 접근 가능한 월을 재구매하지 않도록 차단하는 데 사용 → 구독도 반영)
   */
  const isMonthlyFortuneMonthFullyPurchased = (guestUserId, year, month) => {
    if (!guestUserId) return false;
    const cacheKey = buildMonthlyFortuneCacheKey(guestUserId, year, month);

    // 1순위: access canAccess
    const accessByType = monthlyFortuneAccessByMonth.value[cacheKey];
    if (accessByType && accessByType[10]) {
      if (accessByType[10].canAccess === true) return true;
      return MONTHLY_FORTUNE_CHILD_TYPE_IDS.every((tid) => accessByType[tid]?.canAccess === true);
    }

    // 폴백: 기존 로그 판단
    const monthLogs = monthlyFortuneLogsByMonth.value[cacheKey];
    if (!monthLogs) return false;
    if (monthLogs[10]?.length > 0) return true;
    return MONTHLY_FORTUNE_CHILD_TYPE_IDS.every((tid) => monthLogs[tid]?.length > 0);
  };

  /**
   * 낭만월운 구매이력이 있는 달 목록
   * - 전체구매(typeId 10) 든 자식(42~46) 일부 든, 어떤 typeId 라도 구매한 달이면 포함
   * - 낮은 달이 위로 오는 오름차순 정렬
   */
  const getMonthlyFortunePurchasedMonths = (guestUserId) => {
    if (!guestUserId) return [];
    const logs = monthlyFortunePurchaseHistory.value[guestUserId] || [];
    const monthSet = new Set();
    logs.forEach((log) => {
      if (!log.year || !log.month) return;
      monthSet.add(`${log.year}-${log.month}`);
    });
    const result = Array.from(monthSet).map((key) => {
      const [year, month] = key.split('-').map(Number);
      return { year, month };
    });
    result.sort((a, b) => a.year - b.year || a.month - b.month);
    return result;
  };

  /**
   * 상세분석 접근 자격 (현재 저장사주 기준, typeId 별 분리 저장)
   * 구조: { [analysisTypeId]: accessItem }
   *   - accessItem: { analysisTypeId, canAccess, source, analysisLogs, subscription }
   *   - /analysis/access 응답을 typeId 로 색인. 분석 이용내역 + 구독 권한을 함께 반영.
   *   - analysisLogByType 과 동일하게 평면 맵이며, 저장사주 변경 시 재조회로 덮어쓴다.
   */
  const analysisAccessByType = ref({});
  let contextEpoch = 0;
  let contextGuestUserId = null;
  const logRequests = new Map();
  const resetAnalysisContext = (guestUserId = null) => {
    contextGuestUserId = Number(guestUserId) || null;
    contextEpoch += 1;
    logRequests.clear();
    analysisLogData.value = null;
    analysisLogByType.value = {};
    analysisAccessByType.value = {};
  };

  /**
   * 상세분석 접근 자격 일괄 조회
   * - 여러 분석 타입의 접근 가능 여부(canAccess)를 한 번에 받아 typeId 별로 저장.
   * - 타입별 /analysis/log 반복 호출을 대체하는 권한 확인용.
   * @param {number} guestUserId - 저장사주 ID
   * @param {number[]} analysisTypeIds - 조회할 분석 타입 ID 목록
   * @param {Object} [opts] - { includeChildTypes, year, month, day } 선택 파라미터
   */
  const fetchAnalysisAccess = async (guestUserId, analysisTypeIds = [], opts = {}) => {
    if (contextGuestUserId && Number(guestUserId) !== contextGuestUserId) return [];
    const epoch = contextEpoch;
    if (!guestUserId) return;
    try {
      const params = { guestUserId };
      if (analysisTypeIds?.length) params.analysisTypeIds = analysisTypeIds;
      if (opts.includeChildTypes) params.includeChildTypes = true;
      if (opts.year) params.year = opts.year;
      if (opts.month) params.month = opts.month;
      if (opts.day) params.day = opts.day;

      const response = await getAnalysisAccessAPI(params);
      if (epoch !== contextEpoch) return [];
      const items = response?.items || [];
      const byType = { ...analysisAccessByType.value };
      items.forEach((item) => {
        byType[item.analysisTypeId] = item;
      });
      analysisAccessByType.value = byType;
      return items;
    } catch (e) {
      // 실패 시 기존 접근 자격 캐시는 유지 (권한 판단은 logData 폴백으로 동작)
      return [];
    }
  };

  /**
   * 특정 typeId 의 접근 자격 item 조회 (없으면 null)
   */
  const getAccessItem = (typeId) => {
    return analysisAccessByType.value[typeId] || null;
  };

  /**
   * 상세분석 이용내역 조회
   * @param {number} guestUserId - 게스트 사용자 ID
   * @param {number|null} analysisTypeId - 분석 타입 ID (null이면 전체 조회)
   * @param {string|null} category - 카테고리 필터 (예: 'LOVER', 'FRIEND', 'BUSINESS', 'FAMILY')
   */
  const getAnalysisLog = async (guestUserId, analysisTypeId = null, category = null) => {
    if (contextGuestUserId && Number(guestUserId) !== contextGuestUserId) return;
    const epoch = contextEpoch;
    const requestKey = `${analysisTypeId}:${category}`;
    const request = Symbol();
    logRequests.set(requestKey, request);
    const isCurrent = () => epoch === contextEpoch && logRequests.get(requestKey) === request;
    try {
      const params = { isValid: true };
      if (guestUserId) params.guestUserId = guestUserId;
      if (analysisTypeId) params.analysisTypeId = analysisTypeId;
      if (category) params.category = category;

      const cacheKey = category ? `${analysisTypeId}_${category}` : analysisTypeId;

      const response = await getAnalysisLogAPI(params);
      if (!isCurrent()) return;

      if (response?.content) {
        if (analysisTypeId) {
          // 특정 타입만 조회한 경우 해당 키에 저장
          analysisLogByType.value[cacheKey] = contextGuestUserId && Number(analysisTypeId) === 38
            ? response.content.filter((log) => getHarmonyBaseId(log) === contextGuestUserId)
            : response.content;
        } else {
          // 전체 조회한 경우 기존 방식 유지 (하위 호환)
          analysisLogData.value = response.content;
        }
      }
    } catch (e) {
      if (!isCurrent()) return;
      if (analysisTypeId) {
        const cacheKey = category ? `${analysisTypeId}_${category}` : analysisTypeId;
        analysisLogByType.value[cacheKey] = [];
      } else {
        analysisLogData.value = null;
      }
    } finally {
      if (isCurrent()) loadingTicketInfo.value = false;
    }
  };

  return {
    selectedMenu,
    getSelectedMenu,
    isMenuOpen,
    resetSelectedMenu,
    clickMainMenu,
    toggleSubmenu,
    analysisResult,
    setAnalysisResult,
    resetAnalysisResult,
    analStastResult,
    setAnalStastResult,
    resetAnalstastResult,
    userName,
    setUserName,
    selectedContentIndex,
    setSelectedContentIndex,
    navigateToContent,
    selectChildMenu,
    // 성향분석
    personalityIndex,
    setPersonalityIndex,
    // 용신분석
    yongsinIndex,
    setYongsinIndex,
    // 낭만월운
    monthlyFortuneIndex,
    setMonthlyFortuneIndex,
    // 신년운세
    yearlyFortuneIndex,
    setYearlyFortuneIndex,
    // 낭만궁합
    nangmanHarmonyIndex,
    setNangmanHarmonyIndex,
    harmonyPaymentData,
    setHarmonyPaymentData,
    resetHarmonyPaymentData,
    harmonyExtrasAutoLoading,
    setHarmonyExtrasAutoLoading,
    harmonyResetSelection,
    requestHarmonyResetSelection,
    consumeHarmonyResetSelection,
    harmonySelectedCategory,
    harmonyPersonCount,
    setHarmonySelectionState,
    harmonyIsPurchased,
    harmonyMatchedLog,
    setHarmonyPurchaseState,
    harmonyPaymentPopupTrigger,
    triggerHarmonyPaymentPopup,
    harmonyCategoryPaymentTarget,
    triggerHarmonyCategoryPayment,
    clearHarmonyCategoryPaymentTarget,
    harmonyPurchasedExtraIds,
    harmonyExtraContents,
    setHarmonyPurchasedExtraIds,
    setHarmonyExtraContent,
    resetHarmonyExtras,
    harmonyExtraPaymentTarget,
    triggerHarmonyExtraPayment,
    clearHarmonyExtraPaymentTarget,
    harmonyExtraYearlyPrice,
    harmonyExtraMonthlyPrice,
    setHarmonyExtraPrices,
    // 용신라이트
    yongsinLightIndex,
    setYongsinLightIndex,
    yongsinLightApiData,
    setYongsinLightApiData,
    // 상세분석 이용내역
    analysisLogData,
    analysisLogByType,
    getAnalysisLog,
    resetAnalysisContext,
    // 상세분석 접근 자격 (canAccess: 분석권 + 구독 권한 통합)
    analysisAccessByType,
    fetchAnalysisAccess,
    getAccessItem,
    // 낭만월운 월별 권한
    monthlyFortuneLogsByMonth,
    fetchMonthlyFortuneLogs,
    monthlyFortuneAccessByMonth,
    fetchMonthlyFortuneAccess,
    hasMonthlyFortuneAccess,
    hasMonthlyFortuneLogsCache,
    // 낭만월운 구매이력 (전체)
    monthlyFortunePurchaseHistory,
    fetchMonthlyFortunePurchaseHistory,
    isMonthlyFortuneMonthFullyPurchased,
    getMonthlyFortunePurchasedMonths,
    // 낭만월운 선택 월 (Cover / Panel 공유)
    monthlyFortuneSelectedYear,
    monthlyFortuneSelectedMonth,
    setMonthlyFortuneSelectedMonth,
    // 결제 직후 cover remount 시 drawer 자동 재오픈용 임시 카드
    monthlyFortunePendingDrawerCard,
    setMonthlyFortunePendingDrawerCard,
    consumeMonthlyFortunePendingDrawerCard,
    monthlyFortunePreserveSelectedMonth,
    setMonthlyFortunePreserveSelectedMonth,
    consumeMonthlyFortunePreserveSelectedMonth,
    monthlyFortuneOpenDrawerRequest,
    monthlyFortuneRequestedDrawerCardKey,
    requestMonthlyFortuneOpenDrawer,
  };
});

export const menuItems = [
  {
    id: 'dashboard',
    label: '대시보드',
    view: '/dashboard',
  },
  {
    id: 'saju-summary',
    label: '사주풀이',
    view: '/summary',
  },
  {
    id: 'personality',
    label: '성향분석',
    view: '/personality',
    children: [
      // { id: '', label: '📖 성향분석 안내서 - 당신의 성향을 이해하는 다섯가지 시선', view: '/personality/guide' },
      // { id: '', label: '사주명리의 시선으로 바라 본, 나를 구성하는 오행의 기운 흐름', view: '/personality/content' },
    ],
  },
  {
    id: 'yongsin-analysis',
    label: '용신분석',
    view: '/youngsin',
  },
  {
    id: 'yongsin-light',
    label: '나를 돕는 기운',
    view: '/ys-light',
  },
  {
    id: 'monthly-fortune',
    label: '낭만월운',
    view: '/monthly-fortune',
  },
  {
    id: 'yearly-fortune',
    label: '신년운세',
    view: '/yearly-fortune',
  },
  {
    id: 'luck-analysis',
    label: '운세분석',
    view: '/luck-analysis',
  },
  {
    id: 'five',
    label: '오행도분석',
    view: '/five',
    children: [
      { id: '', label: '나의 오행도', view: '/five/my' },
      { id: '', label: '음양오행', view: '/five/lunar' },
      { id: '', label: '합/충/형/파/해', view: '/five/hap' },
      { id: '', label: '대운', view: '/five/deaun' },
      { id: '', label: '용신', view: '/five/sin' },
    ],
  },
  {
    id: 'energy-compatibility',
    label: '에너지궁합',
    view: '/energy-compatibility',
  },
  {
    id: 'life-calendar',
    label: '라이프 캘린더',
    view: '/life-calendar',
  },
  {
    id: 'five-guuide',
    label: '오행도란?',
    view: '/five-guide',
  },
  {
    id: 'myfive',
    label: '나의 오행도',
    view: '/myfive',
  },
];
